/**
 * recommendationEventService.ts
 * P3: Client-side service for tracking recommendation lifecycle events.
 *
 * Sends events to POST /lender/recommendation-events.
 * Fire-and-forget — failures are logged but never block the UI.
 *
 * Privacy: No member PII sent. Only recommendation-level metadata
 * (recommendation_key, event_type, aggregate counts).
 */

// Match lenderApiService.ts pattern
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

export type RecommendationEventType = 'shown' | 'clicked' | 'dismissed' | 'accepted';

export interface RecommendationEvent {
  recommendation_key: string;
  event_type: RecommendationEventType;
  source?: string;
  segment_key?: string;
  metric_key?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Send a recommendation event to the API (fire-and-forget).
 * Never throws — failures are silently logged.
 */
export async function trackRecommendationEvent(event: RecommendationEvent): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/verascore/lender/recommendation-events`, {
      method: 'POST',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        'x-request-purpose': 'recommendation_tracking',
      },
      credentials: 'include',
      body: JSON.stringify(event),
    });

    if (!response.ok && import.meta.env.DEV) {
      console.warn(`[recommendation-events] API returned ${response.status} for ${event.event_type}:${event.recommendation_key}`);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[recommendation-events] Failed to send event:', err);
    }
    // Fire-and-forget: never throw
  }
}

/**
 * Track that a recommendation card was shown to the lender.
 * Should be called once per card render (debounced by caller).
 */
export function trackRecommendationShown(
  recommendationKey: string,
  metadata?: Record<string, unknown>,
): void {
  trackRecommendationEvent({
    recommendation_key: recommendationKey,
    event_type: 'shown',
    source: 'vs_actions',
    metadata,
  });
}

/**
 * Track that a lender clicked on a recommendation card.
 */
export function trackRecommendationClicked(
  recommendationKey: string,
  metadata?: Record<string, unknown>,
): void {
  trackRecommendationEvent({
    recommendation_key: recommendationKey,
    event_type: 'clicked',
    source: 'vs_actions',
    metadata,
  });
}

/**
 * Track that a lender dismissed a recommendation card.
 */
export function trackRecommendationDismissed(
  recommendationKey: string,
  metadata?: Record<string, unknown>,
): void {
  trackRecommendationEvent({
    recommendation_key: recommendationKey,
    event_type: 'dismissed',
    source: 'vs_actions',
    metadata,
  });
}

/**
 * P4A: Track that a lender accepted/completed a recommendation.
 *
 * This is the trigger for outcome tracking — the backend uses this event
 * to create a frozen baseline snapshot for the affected segment cohort.
 *
 * IMPORTANT: segment_key is REQUIRED for outcome tracking. If not provided,
 * the backend will still record the event but will NOT create a baseline
 * (per P4A.5 guardrails). Callers should always provide segment_key when
 * it is available from the recommendation context.
 *
 * @param recommendationKey - Stable identifier (e.g., 'HIGH_FINANCIAL_STRESS_SUPPORT')
 * @param segmentKey - Cohort boundary (e.g., 'high_financial_stress'). Required for outcome tracking.
 * @param metadata - Optional additional context (role, metric counts, etc.)
 */
export function trackRecommendationAccepted(
  recommendationKey: string,
  segmentKey?: string,
  metadata?: Record<string, unknown>,
): void {
  trackRecommendationEvent({
    recommendation_key: recommendationKey,
    event_type: 'accepted',
    source: 'vs_actions',
    segment_key: segmentKey,
    metadata,
  });
}
